/**
 * G2eGente - Área do Psicólogo / Profissional Credenciado
 * Acesso às respostas individuais, entrevistas e análises por setor
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { nr01API } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  Building2,
  Users,
  ClipboardCheck,
  FileText,
  Loader2,
  Brain,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  User,
  Calendar,
  Eye,
  Save,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

export default function NR01PsychologistArea() {
  const navigate = useNavigate();
  const { diagnosticId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedSetor, setSelectedSetor] = useState(null);
  const [setorResponses, setSetorResponses] = useState(null);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [activeTab, setActiveTab] = useState('respostas');
  
  // Interview form
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [interviewForm, setInterviewForm] = useState({
    notas: '',
    observacoes_comportamentais: '',
    fatores_risco_identificados: [],
    recomendacoes_individuais: ''
  });
  const [savingInterview, setSavingInterview] = useState(false);
  
  // Analysis form
  const [analysisForm, setAnalysisForm] = useState({
    analise_geral: '',
    pontos_criticos: [],
    acoes_recomendadas: [],
    prioridade: 'media',
    observacoes: ''
  });
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  
  // Expanded responses
  const [expandedResponses, setExpandedResponses] = useState({});

  useEffect(() => {
    fetchData();
  }, [diagnosticId]);

  const fetchData = async () => {
    try {
      const { data: result } = await nr01API.getPsychologistSectors(diagnosticId);
      setData(result);
      if (result.setores?.length > 0) {
        setSelectedSetor(result.setores[0].setor);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSetor) {
      fetchSetorResponses();
    }
  }, [selectedSetor]);

  const fetchSetorResponses = async () => {
    setLoadingResponses(true);
    try {
      const { data: result } = await nr01API.getPsychologistResponses(diagnosticId, selectedSetor);
      setSetorResponses(result);
      
      // Load existing analysis if available
      const setorInfo = data?.setores?.find(s => s.setor === selectedSetor);
      if (setorInfo?.analise) {
        setAnalysisForm({
          analise_geral: setorInfo.analise.analise_geral || '',
          pontos_criticos: setorInfo.analise.pontos_criticos || [],
          acoes_recomendadas: setorInfo.analise.acoes_recomendadas || [],
          prioridade: setorInfo.analise.prioridade || 'media',
          observacoes: setorInfo.analise.observacoes || ''
        });
      } else {
        setAnalysisForm({
          analise_geral: '',
          pontos_criticos: [],
          acoes_recomendadas: [],
          prioridade: 'media',
          observacoes: ''
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar respostas');
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleSaveInterview = async () => {
    if (!selectedEmployee || !interviewForm.notas.trim()) {
      toast.error('Selecione um funcionário e preencha as notas');
      return;
    }
    
    setSavingInterview(true);
    try {
      await nr01API.savePsychologistInterview(diagnosticId, {
        employee_id: selectedEmployee.id,
        setor: selectedSetor,
        ...interviewForm
      });
      toast.success('Entrevista salva com sucesso');
      setInterviewForm({
        notas: '',
        observacoes_comportamentais: '',
        fatores_risco_identificados: [],
        recomendacoes_individuais: ''
      });
      setSelectedEmployee(null);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Erro ao salvar entrevista');
    } finally {
      setSavingInterview(false);
    }
  };

  const handleSaveAnalysis = async () => {
    if (!analysisForm.analise_geral.trim()) {
      toast.error('Preencha a análise geral do setor');
      return;
    }
    
    setSavingAnalysis(true);
    try {
      await nr01API.savePsychologistAnalysis(diagnosticId, {
        setor: selectedSetor,
        ...analysisForm
      });
      toast.success('Análise salva com sucesso');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Erro ao salvar análise');
    } finally {
      setSavingAnalysis(false);
    }
  };

  const toggleResponseExpand = (idx) => {
    setExpandedResponses(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const getSetorInfo = () => data?.setores?.find(s => s.setor === selectedSetor);
  
  const RISK_COLORS = {
    alta: '#EF4444',
    media: '#F59E0B',
    baixa: '#10B981'
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/company/nr01/${diagnosticId}/results`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Brain className="w-7 h-7 text-purple-600" />
            Área do Psicólogo
          </h1>
          <p className="text-slate-500">{data?.titulo}</p>
        </div>
        <Badge className="bg-purple-100 text-purple-700">
          {data?.total_respostas} respostas totais
        </Badge>
      </div>

      {/* Sector Selector */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="font-medium">Setor/Ambiente:</Label>
            <Select value={selectedSetor} onValueChange={setSelectedSetor}>
              <SelectTrigger className="w-64" data-testid="sector-select">
                <SelectValue placeholder="Selecione um setor" />
              </SelectTrigger>
              <SelectContent>
                {data?.setores?.map(s => (
                  <SelectItem key={s.setor} value={s.setor}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {s.setor}
                      <Badge variant="outline" className="ml-2 text-xs">
                        {s.total_respostas} resp.
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {getSetorInfo()?.analise_concluida && (
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Análise Concluída
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedSetor && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="respostas" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Respostas Individuais
            </TabsTrigger>
            <TabsTrigger value="entrevistas" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Entrevistas ({getSetorInfo()?.entrevistas_realizadas || 0})
            </TabsTrigger>
            <TabsTrigger value="analise" className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" />
              Análise do Setor
            </TabsTrigger>
          </TabsList>

          {/* Tab: Respostas Individuais */}
          <TabsContent value="respostas">
            {loadingResponses ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Statistics Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BarChart3 className="w-5 h-5 text-amber-600" />
                      Estatísticas por Pergunta
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={setorResponses?.analise_perguntas?.slice(0, 10) || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="pergunta_id" tick={{ fontSize: 10 }} />
                          <YAxis domain={[0, 5]} />
                          <Tooltip />
                          <Bar dataKey="media" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Individual Responses */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5 text-purple-600" />
                      Respostas Individuais (Anônimas)
                      <Badge variant="outline">{setorResponses?.total_respostas || 0}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                        <p className="text-sm text-yellow-800">
                          <strong>Confidencial:</strong> Estas respostas são 100% anônimas. 
                          Não há forma de identificar o respondente individual.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {setorResponses?.respostas_individuais?.map((resp, idx) => (
                        <div key={idx} className="border rounded-lg">
                          <button
                            onClick={() => toggleResponseExpand(idx)}
                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-purple-600" />
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-slate-800">{resp.respondente}</p>
                                <p className="text-xs text-slate-500">
                                  {resp.respondido_em && new Date(resp.respondido_em).toLocaleDateString('pt-BR')}
                                  {resp.origem === 'link_publico' && ' • Via link público'}
                                </p>
                              </div>
                            </div>
                            {expandedResponses[idx] ? (
                              <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </button>
                          
                          {expandedResponses[idx] && (
                            <div className="px-4 pb-4 border-t bg-slate-50">
                              <div className="pt-4 space-y-2">
                                {resp.respostas?.map((r, rIdx) => {
                                  const pergunta = setorResponses?.analise_perguntas?.find(p => p.pergunta_id === r.pergunta_id);
                                  return (
                                    <div key={rIdx} className="flex items-start gap-3 py-2 border-b border-slate-200 last:border-0">
                                      <div className="flex-1">
                                        <p className="text-sm text-slate-700">{pergunta?.texto || r.pergunta_id}</p>
                                        <p className="text-xs text-slate-500 mt-1">{pergunta?.dimensao}</p>
                                      </div>
                                      <Badge className={`${
                                        r.valor <= 2 ? 'bg-red-100 text-red-700' :
                                        r.valor === 3 ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-emerald-100 text-emerald-700'
                                      }`}>
                                        {r.valor}/5
                                      </Badge>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {(!setorResponses?.respostas_individuais || setorResponses.respostas_individuais.length === 0) && (
                        <div className="text-center py-8 text-slate-500">
                          Nenhuma resposta registrada para este setor
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Tab: Entrevistas */}
          <TabsContent value="entrevistas">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Lista de Funcionários */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                    Funcionários do Setor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-4">
                    Selecione um funcionário para registrar notas de entrevista pessoal.
                  </p>
                  
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {getSetorInfo()?.funcionarios?.map(emp => (
                      <button
                        key={emp.id}
                        onClick={() => setSelectedEmployee(emp)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                          selectedEmployee?.id === emp.id 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-slate-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{emp.nome}</p>
                          <p className="text-sm text-slate-500">{emp.cargo}</p>
                        </div>
                      </button>
                    ))}

                    {(!getSetorInfo()?.funcionarios || getSetorInfo().funcionarios.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        Nenhum funcionário cadastrado neste setor
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Formulário de Entrevista */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                    Registro de Entrevista
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedEmployee ? (
                    <div className="space-y-4">
                      <div className="bg-purple-50 rounded-lg p-3">
                        <p className="font-medium text-purple-800">{selectedEmployee.nome}</p>
                        <p className="text-sm text-purple-600">{selectedEmployee.cargo}</p>
                      </div>

                      <div>
                        <Label>Notas da Entrevista *</Label>
                        <Textarea
                          placeholder="Registre as principais observações da entrevista..."
                          value={interviewForm.notas}
                          onChange={(e) => setInterviewForm(prev => ({ ...prev, notas: e.target.value }))}
                          rows={4}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Observações Comportamentais</Label>
                        <Textarea
                          placeholder="Linguagem corporal, tom de voz, sinais de estresse..."
                          value={interviewForm.observacoes_comportamentais}
                          onChange={(e) => setInterviewForm(prev => ({ ...prev, observacoes_comportamentais: e.target.value }))}
                          rows={2}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Fatores de Risco Identificados</Label>
                        <Input
                          placeholder="Separe por vírgula (ex: sobrecarga, conflitos, falta de autonomia)"
                          value={interviewForm.fatores_risco_identificados.join(', ')}
                          onChange={(e) => setInterviewForm(prev => ({ 
                            ...prev, 
                            fatores_risco_identificados: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          }))}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Recomendações Individuais</Label>
                        <Textarea
                          placeholder="Sugestões específicas para este funcionário..."
                          value={interviewForm.recomendacoes_individuais}
                          onChange={(e) => setInterviewForm(prev => ({ ...prev, recomendacoes_individuais: e.target.value }))}
                          rows={2}
                          className="mt-1"
                        />
                      </div>

                      <Button 
                        onClick={handleSaveInterview}
                        disabled={savingInterview}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {savingInterview ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Salvar Entrevista
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Selecione um funcionário para registrar a entrevista</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Análise do Setor */}
          <TabsContent value="analise">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardCheck className="w-5 h-5 text-amber-600" />
                  Análise de Riscos - {selectedSetor}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <Label>Análise Geral do Setor *</Label>
                    <Textarea
                      placeholder="Descreva a análise geral dos riscos psicossociais identificados neste setor..."
                      value={analysisForm.analise_geral}
                      onChange={(e) => setAnalysisForm(prev => ({ ...prev, analise_geral: e.target.value }))}
                      rows={5}
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Pontos Críticos Identificados</Label>
                      <Textarea
                        placeholder="Liste os principais pontos críticos (um por linha)"
                        value={analysisForm.pontos_criticos.join('\n')}
                        onChange={(e) => setAnalysisForm(prev => ({ 
                          ...prev, 
                          pontos_criticos: e.target.value.split('\n').filter(Boolean)
                        }))}
                        rows={4}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Ações Recomendadas</Label>
                      <Textarea
                        placeholder="Liste as ações recomendadas (uma por linha)"
                        value={analysisForm.acoes_recomendadas.join('\n')}
                        onChange={(e) => setAnalysisForm(prev => ({ 
                          ...prev, 
                          acoes_recomendadas: e.target.value.split('\n').filter(Boolean)
                        }))}
                        rows={4}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Prioridade de Intervenção</Label>
                      <Select 
                        value={analysisForm.prioridade} 
                        onValueChange={(v) => setAnalysisForm(prev => ({ ...prev, prioridade: v }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alta">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-red-500 rounded-full" />
                              Alta - Intervenção Imediata
                            </div>
                          </SelectItem>
                          <SelectItem value="media">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                              Média - Monitoramento
                            </div>
                          </SelectItem>
                          <SelectItem value="baixa">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                              Baixa - Manutenção
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Observações Adicionais</Label>
                      <Textarea
                        placeholder="Outras observações relevantes..."
                        value={analysisForm.observacoes}
                        onChange={(e) => setAnalysisForm(prev => ({ ...prev, observacoes: e.target.value }))}
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <Separator />

                  <Button 
                    onClick={handleSaveAnalysis}
                    disabled={savingAnalysis}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    size="lg"
                  >
                    {savingAnalysis ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Salvar Análise do Setor
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
